/**
 * 表示言語のサーバー個人設定 (admin-english-toggle-20260924 / ADR-081)。
 *
 * - 読み: 既存の個人設定 `GET /admin/users/me/preferences` の `preferences.locale`
 *   (対応言語ならそれ、無ければ null)。
 * - 書き: **表示言語だけを更新する `PUT /admin/users/me/locale`** (`{ locale }`)。BE は `locale` 属性だけを
 *   UpdateItem で書き、他の個人設定 (テーマ・通知) に触れない。
 *   個人設定の `PUT /admin/users/me/preferences` は**丸ごと置き換える**ため使わない — GET → 重ねて PUT
 *   にすると、その間に個人設定画面や別タブが保存した値を古い値で巻き戻す。
 * - 個人設定への書き込み (ヘッダーの切替の `PUT /admin/users/me/locale` と、個人設定画面の「保存」の
 *   `PUT /admin/users/me/preferences`) は **同じ列で呼んだ順に 1 本ずつ**送る (`runPreferenceWrite`)。
 *   別々に送ると到着順が入れ替わり、後から押した言語を先に押した「保存」の古い言語で巻き戻す。
 * - 列で待っている間に利用者 (userId / orgId / role / 対象組織) が替わったら、待っていた書き込みは
 *   **送らずに捨てる**。`apiFetch` は送る時点の認証を使うので、送ると次の利用者の個人設定へ書く。
 */
import { apiFetch } from '@/api/client';
import { getIdentityScope } from '@/api/fetchCache';
import { isLocale, type Locale } from '@/i18n';
import type {
  UpdateMyLocaleRequest,
  UpdateMyLocaleResponse,
  UserPreferencesResponse,
} from '@/types/api-types';

const PREFERENCES_PATH = '/admin/users/me/preferences';
const LOCALE_PATH = '/admin/users/me/locale';

/**
 * 列で待っている間に利用者が替わったため、送らずに捨てた個人設定の書き込み。
 * 次の利用者に「保存できませんでした」を見せないよう、呼び出し側はこれを失敗として知らせない。
 */
export class PreferenceWriteCancelledError extends Error {
  constructor() {
    super('利用者が替わったため、個人設定の保存を送りませんでした');
    this.name = 'PreferenceWriteCancelledError';
  }
}

/** サーバー個人設定の表示言語。未設定・未知の値は null。通信失敗は throw。 */
async function loadServerLocale(): Promise<Locale | null> {
  const res = await apiFetch<UserPreferencesResponse>(PREFERENCES_PATH);
  const value = res.preferences?.locale;
  return isLocale(value) ? value : null;
}

/** サーバー個人設定の表示言語だけを更新する。失敗は throw。 */
async function putServerLocale(locale: Locale): Promise<void> {
  const request: UpdateMyLocaleRequest = { locale };
  await apiFetch<UpdateMyLocaleResponse>(LOCALE_PATH, {
    method: 'PUT',
    body: JSON.stringify(request),
  });
}

/** 前の書き込みが終わるまで次の書き込みを待たせる列 (個人設定の書き込み全部で 1 本)。 */
let writeQueue: Promise<void> = Promise.resolve();

/**
 * 個人設定への書き込みを、呼んだ順に 1 本ずつ行う。
 * - 積んだ時点の利用者 (identity) を覚え、順番が来たときに利用者が替わっていれば送らずに
 *   `PreferenceWriteCancelledError` で終える (既に送った書き込みはそのまま)。
 * - 失敗しても列は止めない (次の書き込みは行う)。失敗は呼び出し側へ throw する。
 */
function runPreferenceWrite<T>(write: () => Promise<T>): Promise<T> {
  const identity = getIdentityScope();
  const run = writeQueue.then(() => {
    if (getIdentityScope() !== identity) throw new PreferenceWriteCancelledError();
    return write();
  });
  writeQueue = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/**
 * 表示言語だけを保存する (ヘッダーの切替)。続けて押しても呼んだ順に 1 本ずつ送り、最後に選んだ
 * 言語が残る。利用者の交代で送らなかった分は、何もせずに終える (次の利用者に失敗を知らせない)。
 */
async function saveServerLocale(locale: Locale): Promise<void> {
  try {
    await runPreferenceWrite(() => putServerLocale(locale));
  } catch (err) {
    if (err instanceof PreferenceWriteCancelledError) return;
    throw err;
  }
}

export interface LocalePreferenceApi {
  loadServerLocale: () => Promise<Locale | null>;
  saveServerLocale: (locale: Locale) => Promise<void>;
  /** 個人設定への書き込み (個人設定画面の「保存」) を、表示言語の保存と同じ列で送る。 */
  runPreferenceWrite: <T>(write: () => Promise<T>) => Promise<T>;
}

const LOCALE_PREFERENCE_API: LocalePreferenceApi = {
  loadServerLocale,
  saveServerLocale,
  runPreferenceWrite,
};

/**
 * 表示言語のサーバー同期に使う関数の組。参照は常に同じ (Provider の effect を貼り直さない)。
 */
export function useLocalePreference(): LocalePreferenceApi {
  return LOCALE_PREFERENCE_API;
}
